import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth/auth.service';
import { PaymentMethod, PaymentService } from '../../services/payment/payment.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';

@Component({
  selector: 'app-saved-payment-methods',
  imports: [TranslatePipe],
  templateUrl: './saved-payment-methods.component.html',
  styleUrl: './saved-payment-methods.component.scss',
})
export class SavedPaymentMethodsComponent {
  public paymentService = inject(PaymentService);

  public cacheService = inject(CacheService);

  public authService = inject(AuthService);

  public subscriberService = inject(SubscriberService);

  public selectedPaymentMethod = output<PaymentMethod>();

  public selectedPaymentMethodAction = output<{
    action: 'set-default' | 'delete';
    paymentMethod: PaymentMethod;
  }>();

  public selectedPaymentMethodId = input<string | null>(null);

  public paymentMethodExplicitlyCleared = input<boolean>(false);

  public accordionMode = input<boolean>(false);

  public displayMode = input<
    'all' | 'default-only' | 'non-default-only' | 'selected-only' | 'selected-excluded'
  >('all');

  public selectedPaymentCard = signal<PaymentMethod | null>(null);

  public displayedPaymentMethods = computed(() => {
    const methods = this.paymentMethods();

    const mode = this.displayMode();

    if (mode === 'all') return methods;

    const defaultMethod = methods.find((m) => m.default);
    if (mode === 'default-only') return defaultMethod ? [defaultMethod] : [];

    if (mode === 'non-default-only') {
      if (!defaultMethod) return methods;
      return methods.filter((m) => m._id !== defaultMethod._id);
    }

    if (mode === 'selected-only') {
      const id = this.selectedPaymentMethodId();
      if (!id) return [];
      const method = methods.find((m) => m._id === id);
      return method ? [method] : [];
    }

    if (mode === 'selected-excluded') {
      const id = this.selectedPaymentMethodId();
      if (!id) return methods;
      return methods.filter((m) => m._id !== id);
    }
    return methods;
  });

  public effectiveSelectedId = computed(() => {
    const id = this.selectedPaymentMethodId();
    if (id != null) return id;
    if (this.paymentMethodExplicitlyCleared()) return null;
    const methods = this.paymentMethods();
    const defaultMethod = methods.find((m) => m.default);
    return defaultMethod?._id ?? null;
  });

  public expandedId = signal<string | null>(null);

  public s3BucketAssets = computed(
    () => this.cacheService.cachedContent()?.cache?.config?.webAppConfig?.s3BucketAssets
  );

  public paymentMethods = computed(() => this.paymentService.paymentMethods());

  public subscriberId = computed(() => this.subscriberService.subscriber()._id || null);

  public isAuthenticated = computed(() => this.authService.isAuthenticated());

  private lastProcessedSubscriberId: string | null = null;

  public customerId = computed(() => {
    return this.cacheService.cachedContent()?.cache?.customerId;
  });

  constructor() {
    effect(() => {
      const subscriberId = this.subscriberId();
      const isAuthenticated = this.isAuthenticated();

      // Clear cache when user logs out
      if (!isAuthenticated) {
        if (this.lastProcessedSubscriberId !== null) {
          this.paymentService.clearPaymentMethodsCache();
          this.lastProcessedSubscriberId = null;
        }
        return;
      }

      // Only proceed if user is logged in and has a subscriberId
      if (!subscriberId || !isAuthenticated) {
        return;
      }

      // Check if subscriber changed - clear cache if different user
      if (
        this.lastProcessedSubscriberId !== null &&
        this.lastProcessedSubscriberId !== subscriberId
      ) {
        this.paymentService.clearPaymentMethodsCache();
      }

      const isLoading = this.paymentService.isPaymentMethodsLoading();
      const isCacheInitialized = this.paymentService.isPaymentMethodsCacheInitialized(subscriberId);

      // Only load if not loading and cache not initialized for this subscriber
      if (!isLoading && !isCacheInitialized) {
        this.paymentService.loadPaymentMethods(subscriberId).subscribe({
          next: () => {
            this.lastProcessedSubscriberId = subscriberId;
          },
          error: (err) => {
            console.error('Error loading payment methods:', err);
            this.lastProcessedSubscriberId = subscriberId;
          },
        });
      } else if (isCacheInitialized) {
        this.lastProcessedSubscriberId = subscriberId;
      }
    });

    effect(() => {
      const selectedId = this.effectiveSelectedId();
      if (selectedId === null) {
        this.selectedPaymentCard.set(null);
      } else {
        const methods = this.paymentMethods();
        const method = methods.find((m) => m._id === selectedId);
        if (method) {
          this.selectedPaymentCard.set(method);
        } else {
          this.selectedPaymentCard.set(null);
        }
      }
    });
  }

  /**
   * Formats payment method expiry to ensure two-digit month
   * @param expiry - Expiry string in format "M/YY" or "MM/YY"
   * @returns Formatted expiry string in format "MM/YY"
   */
  formatExpiry(expiry: string): string {
    if (!expiry) return '';

    const parts = expiry.split('/');
    if (parts.length !== 2) return expiry;

    const month = parts[0].padStart(2, '0');
    const year = parts[1];

    return `${month}/${year}`;
  }

  onPaymentMethodSelect(paymentMethod: PaymentMethod): void {
    this.selectedPaymentCard.set(paymentMethod);
    this.selectedPaymentMethod.emit(paymentMethod);
  }

  toggleAccordion(id: string): void {
    this.expandedId.update((current) => (current === id ? null : id));
  }

  onPaymentMethodAction(action: 'set-default' | 'delete', paymentMethod: PaymentMethod): void {
    this.selectedPaymentMethodAction.emit({ action, paymentMethod });
  }

  public getBrandLogoUrl(brand: string): string {
    const s3 = this.s3BucketAssets();
    const customerId = this.customerId();
    return `${s3}/${customerId}/web/plan-purchase/${brand}-logo.png`;
  }
}
